import { useMemo, useState } from "react";

import { colorsPalettes } from "@similarweb/styles";
import { CircleSwitcherItem, Switcher } from "@similarweb/ui-components";

import { Legends } from "app/components/React/Legends/Legends";
import { EGridDirections } from "app/components/React/Legends/legendsTypes";
import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, percentageSignFilter } from "app/filters/ngFilters";
import {
    NewVsReturningDropdown,
    transformData as transformDataCompare,
} from "app/pages/website-analysis/new-vs-returning/NewVsReturningWebAnalysisCompare";
import {
    displayTypeItems,
    NewVsReturningVertical,
    transformData,
} from "app/pages/website-analysis/new-vs-returning/NewVsReturningWebAnalysisSingle";
import { GenericGraph } from "app/pages/website-analysis/TrafficAndEngagement/Components/GenericGraph";
import {
    chartGranularityItemClickArea,
    getGetExcel,
    isTimeGranularityDisabledOnlyMonth,
    OVERVIEW_EXCEL_ENDPOINT,
    dateFormatter,
} from "app/pages/website-analysis/TrafficAndEngagement/UtilityFunctionsAndConstants/UtilityFunctions";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";

import { CompareModeTooltip } from "../ChartTooltips/CommonChartTooltip";
import { tooltipPositioner } from "../MD/chartConfig";

const yAxisFormatters = {
    absolute: minVisitsAbbrFilter(),
    percentage: (value) => percentageSignFilter()(value, 2),
};

export const NewVsReturning = (props: any) => {
    const {
        name,
        meta: { isSingleMode, isNonTotalWebSource, isCategoryActive },
    } = props;
    const [chartType, setChartType] = useState<"absolute" | "percentage">("absolute");
    const [selectedMetric, setSelectedMetric] = useState(NewVsReturningVertical.NewUsers);
    const params = proNavigator.getParams();
    const { webSource } = params;
    const isPercentage = chartType === "percentage";
    const yAxisFormatter = yAxisFormatters[chartType];
    const selectedIndex = isPercentage ? 1 : 0;
    const onItemClick = (index) => {
        const chartTypeName = index === 0 ? "absolute" : "percentage";
        setChartType(chartTypeName);
        allTrackers.dispatchTrackEvent("Measure Button", "click", `Over Time Graph/Traffic Overview/${chartTypeName}`);
    };
    const customGraphSwitcher = useMemo(() => {
        return (
            <Switcher
                customClass="CircleSwitcher"
                className="rightSwitcher"
                selectedIndex={selectedIndex}
                onItemClick={onItemClick}
            >
                {displayTypeItems.map((displayTypeItem) => (
                    <CircleSwitcherItem key={displayTypeItem.name}>{displayTypeItem.display}</CircleSwitcherItem>
                ))}
            </Switcher>
        );
    }, [chartType]);
    const endpoint = "widgetApi/WebNewVsReturning/NewVsReturning/Data";
    const yAxisLabelsFormatter = ({ value }) => yAxisFormatter(value);
    const getExcelLink = getGetExcel(OVERVIEW_EXCEL_ENDPOINT);
    const getSites = ({ compareSites }) =>
        !isSingleMode
            ? compareSites
            : [
                  {
                      name: "New Users",
                      isSelected: true,
                      color: colorsPalettes.blue[400],
                      alias: "New Users",
                      infoTooltip: translate("tae.nvr.legend.newusers.tooltip"),
                  },
                  {
                      name: "Returning Users",
                      isSelected: true,
                      color: colorsPalettes.sky[300],
                      alias: "Returning Users",
                      infoTooltip: translate("tae.nvr.legend.returningusers.tooltip"),
                  },
              ];

    const getGraphData = ({ data, sites, chosenSite }) => {
        const selectedSites = sites.filter(({ isSelected }) => isSelected).map(({ name }) => name);
        if (isSingleMode) {
            const graphData = transformData(data, chosenSite, isPercentage, selectedSites);
            return graphData;
        }
        const graphData = transformDataCompare(data, selectedMetric, isPercentage, sites);
        return graphData;
    };
    const d = [
        {
            position: 0,
            displayName: translate(isSingleMode ? "Metric" : "Domain"),
        },
        {
            position: 1,
            displayName: isPercentage ? "Share" : "Unique Visitors",
        },
        {
            position: 2,
            displayName: translate("website.analysis.traffic.and.engagement.chart.tooltip.change"),
        },
    ];
    const chartTooltip = (props) => {
        return (
            <CompareModeTooltip
                {...props}
                tableHeaders={d}
                xFormatter={dateFormatter}
                yFormatter={({ value }) => yAxisFormatter(value)}
                totalAggregate={isCategoryActive ? null : (...values) => values.reduce((acc, v) => acc + v, 0)}
                showTotal={!isPercentage}
            />
        );
    };

    const singleModeLegends = (props) => {
        const { toggleSeries } = props;

        const onToggleSeries = (seriesName) => {
            toggleSeries(seriesName);
            const addOrRemoveSeries = seriesName.hidden ? "remove" : "add";
            allTrackers.dispatchTrackEvent(
                "Click Filter",
                addOrRemoveSeries,
                `Over Time Graph/Traffic Overview/${seriesName.name}`,
            );
        };

        return (
            <Legends
                {...props}
                textMaxWidth="300px"
                showLegendsData={false}
                gridDirection={EGridDirections.COLUMN}
                toggleSeries={onToggleSeries}
            />
        );
    };
    const legends = isSingleMode ? singleModeLegends : undefined;
    const chartTooltipPositioner = isSingleMode ? tooltipPositioner : undefined;

    const metricData = {
        endpoint,
        isTimeGranularityDisabled: isTimeGranularityDisabledOnlyMonth,
        getChartType: ({ isSingleMode }) => (isSingleMode ? "area" : "line"),
        yAxisLabelsFormatter,
        getExcelLink,
        getSites,
        getGraphData,
        chartTooltip,
        name,
        legends,
        chartGranularityItemClick:
            !isCategoryActive && isSingleMode && !isNonTotalWebSource ? chartGranularityItemClickArea : undefined,
        tooltipPositioner: chartTooltipPositioner,
        hideCategoryBenchmarkDropdown: true,
        customGraphSwitcher,
        maxYAxis: isPercentage ? 1 : null,
        customActionButtons: isSingleMode ? null : (
            <NewVsReturningDropdowWrapper
                selectedMetric={selectedMetric}
                setSelectedMetric={setSelectedMetric}
            />
        ),
        hideLegendsData: true,
    };
    return (
        <GenericGraph
            metric={metricData}
            {...props}
        />
    );
};

const NewVsReturningDropdowWrapper = ({ selectedMetric, setSelectedMetric }) => {
    const onDropdownChanged = ({ id }: { id: "NewUsers" | "ReturningUsers" }) => {
        setSelectedMetric(NewVsReturningVertical[id]);

        const idTextForTracking = id === "NewUsers" ? "New Users" : "Returning Users";
        allTrackers.dispatchTrackEvent("Dropdown", "Select", `Over Time Graph/Traffic Overview/${idTextForTracking}`);
    };
    return (
        <NewVsReturningDropdown
            currentMetric={selectedMetric}
            onDropdownChanged={onDropdownChanged}
        />
    );
};
